<template>
  <page-layout ref="layout" :tour-props="tours.pageLayout.props">

    <template #breadcrumbs>
      <b-breadcrumb-item text="Support" :to="{ name: 'support' }" />
      <b-breadcrumb-item text="Getting Started" active />
    </template>

    <template #actions>
      <b-button v-if="tours.pageLayout.props.actions.show" id="page-layout-action-create" v-b-tooltip="'Create'" variant="transparent" size="sm">
        <font-awesome-icon icon="fa-solid fa-plus" />
      </b-button>
      <b-button v-if="tours.pageLayout.props.actions.show" id="page-layout-action-save" v-b-tooltip="'Update'" variant="transparent" size="sm">
        <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" />
      </b-button>
    </template>

    <template v-if="tours.pageLayout.props.actions.show" #dropdown-options="{ state }">
      <template>
        <b-dropdown-item id="page-layout-option-edit" @click="state.editing = !state.editing">
          <feather-icon icon="EditIcon"/>
          <span class="align-middle ml-50">Edit</span>
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
      </template>
      <b-dropdown-item id="page-layout-option-refresh">
        <feather-icon icon="RotateCwIcon"/>
        <span class="align-middle ml-50">Refresh</span>
      </b-dropdown-item>
    </template>

    <template #tour="{ tourName }">
      <tour :name="tourName" :steps="tours.pageLayout.steps" :callbacks="tours.pageLayout.callbacks"/>
    </template>

    <template #content>

      <b-alert :show="enabled === false" variant="danger">
        This system is currently disabled, and only visible to those with the administrator privileges.
      </b-alert>

      <b-card id="tour-card">
        <b-container>
          <b-row class="match-height">
            <b-col class="mx-auto" cols="6" >
              <b-card no-body class="border text-center">
                <b-card-body body-class="pb-1">
                  <feather-icon icon="SettingsIcon" size="30" class="mb-1"/>
                  <h4>Page Layout Tour</h4>
                  <b-card-text>Learn the basics of how this application works.</b-card-text>
                </b-card-body>
                <b-card-footer footer-class="border-0 pt-0">
                  <b-link class="bg-light-primary btn" @click="$tours.pageLayout.start()">Start Tour</b-link>
                </b-card-footer>
              </b-card>
            </b-col>
            <b-col class="mx-auto">
              <b-card no-body class="border text-center">
                <b-card-body body-class="pb-1">
                  <feather-icon icon="ColumnsIcon" size="30" class="mb-1"/>
                  <h4>Table Layout Tour</h4>
                  <b-card-text>Learn about the functionality built into our tables.</b-card-text>
                </b-card-body>
                <b-card-footer footer-class="border-0 pt-0">
                  <b-link class="bg-light-primary btn" @click="$tours.tableLayout.start()">Start Tour</b-link>
                </b-card-footer>
              </b-card>
            </b-col>
          </b-row>
        </b-container>

        <tour :name="tours.pageLayout.name" :steps="tours.pageLayout.steps" :callbacks="tours.pageLayout.callbacks"/>
        <tour :name="tours.tableLayout.name" :steps="tours.tableLayout.steps" :callbacks="tours.tableLayout.callbacks"/>
      </b-card>

      <table-layout v-if="tours.tableLayout.table.show" id="tour-table-layout" ref="table-layout"
                    :fields="tours.tableLayout.table.fields"
                    :filters="tours.tableLayout.table.filters" :filters-options="{ visible: true, collapsed: false }"
                    :items="tours.tableLayout.table.items"
                    :loading="tours.tableLayout.table.loading"
                    :paging="tours.tableLayout.table.paging"
                    :tour-props="tours.tableLayout.props">

        <template #filters>
          <b-row>
            <b-col>
              <b-form-group label="School" label-for="school-input">
                <v-select id="school-input"
                          v-model="tours.tableLayout.table.filters.school.value"
                          :options="filterSchoolOptions"
                          :loading="tours.tableLayout.table.loading"
                          :select-on-tab="true"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          class="w-100"/>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group label="Grade" label-for="grade-input">
                <v-select id="grade-input"
                          v-model="tours.tableLayout.table.filters.grade.value"
                          :options="['10', '11']"
                          :searchable="false"
                          :select-on-tab="true"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          class="w-100"/>
              </b-form-group>
            </b-col>
          </b-row>
        </template>

        <template #cell(name)="{data}">
          <b-media vertical-align="center" no-body>
            <b-media-aside>
              <b-avatar variant="primary" size="2.5em" badge-variant="white">
                <font-awesome-icon icon="fas fa-graduation-cap"></font-awesome-icon>
              </b-avatar>
            </b-media-aside>
            <b-media-body class="align-self-center">
              <b-link :to="{ name: 'all-state-student', params: { id: data.item.id } }" class="font-weight-bold d-block text-nowrap">
                {{ data.item.name.first }} {{ data.item.name.last }}
              </b-link>
            </b-media-body>
          </b-media>
        </template>

        <template #cell(row-options)="{data}">
          <b-dropdown-item class="table-row-option-details">
            <feather-icon icon="FileTextIcon" />
            <span class="align-middle ml-50">Details</span>
          </b-dropdown-item>
          <b-dropdown-item class="table-row-option-clone">
            <feather-icon icon="CopyIcon" />
            <span class="align-middle ml-50">Clone</span>
          </b-dropdown-item>
          <b-dropdown-divider/>
          <b-dropdown-item class="table-row-option-delete" @click="$refs.layout.confirmDelete(data.item, null, {
                  confirm: {
                    text: `Deleting a Student will also delete the Student Bill, all associated Applications and Selections.
                    <strong>Be very careful when performing this action.</strong>`,
                    shouldParse: true
                  }
                })">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </template>


      </table-layout>

    </template>
  </page-layout>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import { BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Tour from '@/components/Tour.vue'
import PageLayout from '@/components/PageLayout.vue';
import TableLayout from '@/components/TableLayout.vue';
import vSelect from 'vue-select';

export default {
  name: 'GettingStarted',
  components: {
    TableLayout,
    PageLayout,
    BCardCode,
    BButton,
    Tour,
    vSelect
  },
  directives: {
    Ripple,
  },
  props: {
    enabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tours: {
        pageLayout: {
          name: 'pageLayout',
          steps: [
            {
              target: '.navbar-brand',
              header: { title: 'Logo' },
              content: 'If you ever want to get back to the home page, you can click the logo.',
              params: {
                placement: 'bottom-end',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#menu-toggle',
              header: { title: 'Menu Toggle' },
              content: 'If you need more screen space, you can easily toggle the main menu so that it collapses. Moving your mouse over the collapsed menu will temporarily expand it\'s contents again. ',
              params: {
                placement: 'bottom',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '.navigation.navigation-main',
              header: { title: 'Menu' },
              content: 'The menu contains all of the relevant links you\'ll need to navigate around the application.',
              params: {
                placement: 'right',
                enableScrolling: false,
                highlight: true
              },
            },
            /*{
              target: '.navbar.header-navbar',
              header: { title: 'Navigation Bar' },
              content: 'A breadcrumb navigation provides links back to each previous page the user navigated through, and shows the user\'s current location.',
              params: {
                placement: 'bottom',
                enableScrolling: false,
                highlight: true
              },
            },*/
            {
              target: '.dropdown-user',
              header: { title: 'My Account' },
              content: 'This option allows you to manage your account details.',
              params: {
                placement: 'bottom',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#page-layout-breadcrumbs',
              header: { title: 'Page Breadcrumbs' },
              content: 'A breadcrumb navigation provides links back to each previous page the user navigated through, and shows the user\'s current location.',
              params: {
                placement: 'bottom',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#page-layout-actions',
              header: { title: 'Page Actions' },
              content: `
                    <p>Actions allow you to make changes to the database.</p>
                    <p><small class="text-danger font-italic">These buttons will not always appear, and that means you either do not have permission to perform an action, or you have not completed the prerequisite steps.</small></p>
              `,
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#page-layout-action-create',
              header: { title: 'Create' },
              content: 'Some pages will allow you to create new data. By clicking this button you will either be redirected to a page that handles the creation, or a modal will popup on the current page.',
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#page-layout-action-save',
              header: { title: 'Update' },
              content: 'If you have gone into edit mode and made changes to data, the save button will send that information to the server.',
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
            },
            {
              target: '#page-layout-options__BV_toggle_',
              header: { title: 'Page Options' },
              content: 'The page options button contains a list of options that manipulate the state of the page.',
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = false
                resolve()
              })
            },
            {
              target: '#page-layout-option-edit',
              header: { title: 'Edit' },
              content: 'The edit option allows you to change the page state so that data is editable.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#page-layout-option-refresh',
              header: { title: 'Refresh' },
              content: 'The refresh option allows you to request that the page data be reloaded. This is preferable to reloading from the browser, as it only reloads the data, not the entire application.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#page-layout-option-print',
              header: { title: 'Print' },
              content: 'The print option sets the page into print mode, hiding unnecessary page details from being printed out.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#page-layout-option-help',
              header: { title: 'Help' },
              content: 'If you ever find your self confused, you can click this button to start a tour specific to the page you are on.',
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#page-layout-content',
              header: { title: 'Page Content' },
              content: `
                  <p>The content of a page will change depending on where you are in the application, but it will always be located here.</p>
                  <p><small class="text-danger font-italic">There typically tends to be two types of page content. A list/table page, and a single object page. If you wish to understand specific table functionality please click the <strong>Table Layout Tour</strong> button.</small></p>
              `,
              params: {
                placement: 'bottom-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.pageLayout.props.options.show = false
                resolve()
              })
            },
          ],
          props: {
            actions: { show: false },
            options: { show: false }
          },
          callbacks: {
            onStart: () => {
              this.tours.pageLayout.props.actions.show = true
            },
            onSkip: () => {
              this.tours.pageLayout.props.actions.show = false
              this.tours.pageLayout.props.options.show = false
            },
            onFinish: () => {
              this.tours.pageLayout.props.actions.show = false
              this.tours.pageLayout.props.options.show = false
            },
            onStop: () => {
              this.tours.pageLayout.props.actions.show = false
              this.tours.pageLayout.props.options.show = false
            }
          },
        },
        tableLayout: {
          name: 'tableLayout',
          steps: [
            {
              target: '#tour-table-layout',
              header: { title: 'Table Layout Tour' },
              content: 'If you ever find your self confused, you can click on this code icon to check the source of current demo.',
              params: {
                placement: 'top',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = false
                resolve()
              })
            },
            {
              target: '#table-layout-filters',
              header: { title: 'Table Filters' },
              content: 'Predefined filters that allow you to narrow down table rows. ',
              params: {
                placement: 'top',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                resolve()
              })
            },
            {
              target: '#table-layout-filters .card-action-collapse',
              header: { title: 'Table Filters - Collapse/Expand' },
              content: 'The Table Filters card can be collapsed or expanded by clicking this button.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                resolve()
              })
            },
            {
              target: '#table-layout-actions',
              header: { title: 'Table Actions' },
              content: 'Actions allow you to manipulate the data being displayed in the table.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-action-columns__BV_toggle_',
              header: { title: 'Columns' },
              content: 'The columns action allows you to toggle which fields are displayed in the table.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-action-sorting__BV_button_',
              header: { title: 'Sorting Direction' },
              content: 'The sorting direction action allows you to change the direction in which the data is displayed.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-action-sorting__BV_toggle_',
              header: { title: 'Sorting By Field' },
              content: 'The sorting by action allows you to change which field the table is sorted on.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-action-page-size__BV_toggle_',
              header: { title: 'Page Size' },
              content: 'The page size action allows you to change the number of rows displayed in the table.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-action-filters',
              header: { title: 'Filters' },
              content: 'If the table has predefined filters, this icon will appear. It will allow you to hide the filters card, while still indicating how many filters are applied.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-search',
              header: { title: 'Search' },
              content: 'The search function allows you to search table data, helping you find what you are looking for.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true }
            },
            {
              target: '#table-layout-search-options',
              header: { title: 'Search Options' },
              content: 'The search options allows you to refine how the search function looks for data.',
              params: { placement: 'bottom', enableScrolling: false, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                resolve()
              })
            },
            {
              target: '#table-layout-options__BV_toggle_',
              header: { title: 'Table Options' },
              content: 'The table options menu shows additional table functionality.',
              params: { placement: 'top', enableScrolling: false, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#table-layout-option-print',
              header: { title: 'Print' },
              content: 'The print option sets the table into print mode, hiding unnecessary page details from being printed out.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#table-layout-option-export',
              header: { title: 'Export' },
              content: 'The export option opens a modal and allows you to select table fields to be exported.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#table-layout-option-help',
              header: { title: 'Help' },
              content: 'If you ever find your self confused, you can click this button to start the table layout tour.',
              params: {
                placement: 'left-start',
                enableScrolling: false,
                highlight: true
              },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = true
                resolve()
              })
            },
            {
              target: '#table-container',
              header: { title: 'Table' },
              content: 'The table content will display one of three states. Loading, No Content, or Rows',
              params: { placement: 'top', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                resolve()
              })
            },
            {
              target: '#table-row-options-0',
              header: { title: 'Row Actions' },
              content: 'Some tables will have actions associated to the individual row.',
              params: { placement: 'top', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = false
                resolve()
              })
            },
            {
              target: '.table-row-option-details',
              header: { title: 'Details' },
              content: 'The Details action will take you into individual record.',
              params: { placement: 'left-start', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = true
                resolve()
              })
            },
            {
              target: '.table-row-option-clone',
              header: { title: 'Clone' },
              content: `
                <p>The Clone action will copy all the data of the selected row and redirect you to the create page with the fields filled out.</p>
                <p><small class="text-danger font-italic">This action will only appear on tables with clone functionality enabled.</small></p>
              `,
              params: { placement: 'left-start', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = true
                resolve()
              })
            },
            {
              target: '.table-row-option-delete',
              header: { title: 'Delete' },
              content: `
                <p>The Delete action will remove an item from the database as well as the table.</p>
                <p><small class="text-danger font-italic">This action will only appear to those with the appropriate permissions.</small></p>
              `,
              params: { placement: 'left-start', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = true
                resolve()
              })
            },
            {
              target: '#table-layout-paging-details',
              header: { title: 'Paging Details' },
              content: 'The paging details show the total number of entries in the table, as well as how many of them are being displayed.',
              params: { placement: 'bottom', enableScrolling: true, highlight: true },
              before: type => new Promise((resolve, reject) => {
                this.tours.tableLayout.table.show = true
                this.tours.tableLayout.props.options.show = false
                this.tours.tableLayout.props.row.options.show = false
                resolve()
              })
            },
            {
              target: '#table-layout-paging-pagination',
              header: { title: 'Pagination' },
              content: 'The pagination buttons allow you to change the current page of data being displayed.',
              params: { placement: 'bottom', enableScrolling: true, highlight: true }
            },

          ],
          callbacks: {
            onSkip: () => {
              this.tours.tableLayout.props.options.show = false
              this.tours.tableLayout.props.row.options.show = false
              this.tours.tableLayout.table.show = false
            },
            onFinish: () => {
              this.tours.tableLayout.props.options.show = false
              this.tours.tableLayout.props.row.options.show = false
              this.tours.tableLayout.table.show = false
            },
            onStop: () => {
              this.tours.tableLayout.props.options.show = false
              this.tours.tableLayout.props.row.options.show = false
              this.tours.tableLayout.table.show = false
            }
          },
          props: {
            options: { show: false },
            row: {
              options: {
                index: 0,
                show: false
              }
            }
          },
          table: {
            fields: [
              {
                key: 'name',
                subKeys: ['name.first', 'name.last'],
                label: 'Name',
                sortable: true,
                filterable: true,
                visible: true,
                tdClass: 'align-middle'
              },
              {
                key: 'school.name.legal',
                label: 'School',
                sortable: true,
                filterable: true,
                visible: true,
                tdClass: 'align-middle'
              },
              {
                key: 'grade',
                label: 'Grade',
                sortable: true,
                filterable: true,
                visible: true,
                tdClass: 'align-middle'
              },
              {
                key: 'row-options',
                label: '',
                sortable: false,
                filterable: false,
                visible: true,
                tdClass: ['align-middle', 'table-row-options']
              },
            ],
            filters: {
              school: { key: 'school.name.legal', value: null },
              grade: { key: 'grade', value: null },
            },
            paging: {
              size: 5,
              page: 1,
              options: {
                sizes: [5, 10, 25, 0]
              }
            },
            items: [
              { name: { first: 'John', last: 'Deer'}, school: { name: { legal: 'School 1' } }, grade: 10, },
              { name: { first: 'Dan', last: 'Dog'}, school: { name: { legal: 'School 2' } }, grade: 10, },
              { name: { first: 'Amy', last: 'Bee'}, school: { name: { legal: 'School 2' } }, grade: 11, },
              { name: { first: 'Adam', last: 'Bear'}, school: { name: { legal: 'School 1' } }, grade: 11, },
              { name: { first: 'Brianna', last: 'Sparrow'}, school: { name: { legal: 'School 3' } }, grade: 10, },
              { name: { first: 'Mike', last: 'Bull'}, school: { name: { legal: 'School 1' } }, grade: 10, },
              { name: { first: 'Bill', last: 'Tiger'}, school: { name: { legal: 'School 2' } }, grade: 11, },
              { name: { first: 'Jane', last: 'Lion'}, school: { name: { legal: 'School 1' } }, grade: 10, },
              { name: { first: 'Doug', last: 'Wolf'}, school: { name: { legal: 'School 4' } }, grade: 11, },
              { name: { first: 'Mike', last: 'Horse'}, school: { name: { legal: 'School 1' } }, grade: 10, },
            ],
            loading: false,
            show: false
          }
        }
      },

    }
  },
  computed: {
    filterSchoolOptions() {
      return [...new Set(this.tours.tableLayout.table.items.map(item => item.school?.name?.legal || null)
          .filter(name => name !== null))].sort((a, b) => a.localeCompare(b))
    },
  },
  mounted() {
    this.$refs.layout.state.loading = false
  },
  methods: {

  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/tour.scss';
.v-tour__target--highlighted {
  box-shadow: 0 0 0 .25rem var(--secondary);
  border-radius: 0.358rem;
}
</style>
